// src/components/common/PaginationControls.jsx
import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';

const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
  const { t } = useAppContext();
  const [jumpToPage, setJumpToPage] = useState('');
  
  // Handle jump to page
  const handleJumpToPage = (e) => {
    e.preventDefault();
    const pageNumber = parseInt(jumpToPage, 10);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
      setJumpToPage('');
    }
  };
  // --- Pagination Logic (adapted from MainSearchResults) ---
  const getPaginationRange = () => {
    const range = [];
    const totalPageNumbersToShow = 15; // Increased from 8 to 15 for wider page range
    const halfRange = Math.floor(totalPageNumbersToShow / 2);

    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange - (totalPageNumbersToShow % 2 === 0 ? 1 : 0));

    // Adjust range if near the beginning
    if (currentPage <= halfRange) {
      endPage = Math.min(totalPages, totalPageNumbersToShow);
    }

    // Adjust range if near the end
    if (currentPage + halfRange > totalPages) {
      startPage = Math.max(1, totalPages - totalPageNumbersToShow + 1);
    }

    // Ensure startPage is never less than 1
    startPage = Math.max(1, startPage);

    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }
    return range;
  };
  // --- End Pagination Logic ---

  // Don't render controls if there's only one page or less
  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="flex justify-center items-center mt-4 space-x-1">
      {/* First Page Button */}
      <button
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Go to first page"
      >
        {'<<'}
      </button>
      {/* Previous Page Button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Go to previous page"
      >
        {'<'}
      </button>

      {/* Page Number Buttons */}
      {getPaginationRange().map((pageNumber) => (
        <button
          key={pageNumber}
          onClick={() => onPageChange(pageNumber)}
          disabled={currentPage === pageNumber}
          className={`px-3 py-1 text-sm font-medium border border-gray-300 rounded-md ${
            currentPage === pageNumber
              ? 'bg-blue-500 text-white border-blue-500 cursor-default'
              : 'text-gray-600 bg-white hover:bg-gray-50'
          }`}
          aria-label={`Go to page ${pageNumber}`}
          aria-current={currentPage === pageNumber ? 'page' : undefined}
        >
          {pageNumber}
        </button>
      ))}

      {/* Next Page Button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Go to next page"
      >
        {'>'}
      </button>
      {/* Last Page Button */}
      <button
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Go to last page"
      >
        {'>>'}
      </button>
      
      {/* Jump to Page Form */}
      <form onSubmit={handleJumpToPage} className="flex items-center ml-4">
        <span className="text-sm text-gray-600 mr-2">{t('pagination.jumpTo')}</span>
        <input
          type="text"
          value={jumpToPage}
          onChange={(e) => setJumpToPage(e.target.value)}
          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md"
          placeholder={t('pagination.pagePlaceholder')}
          aria-label="Jump to page"
        />
        <button
          type="submit"
          className="ml-2 px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {t('pagination.go')}
        </button>
      </form>
    </div>
  );
};

export default PaginationControls;
